import type { PipelineRun } from '@clm/shared-types';

import { DataTable, type DataTableColumn } from '../../components/DataTable';
import { summariseFilter, truncate } from './shared';

/**
 * The last twenty terminal runs — CLM-83 (closes B-35).
 *
 * API-originated runs only: a break-glass CLI / `run-pipeline.yml` run writes
 * no `pipeline_run` row (D-83-9), and the caption says so. `failed` rows carry
 * the `bad` tone; a long `error_summary` is cut for the cell with the full text
 * in `title`.
 */

const COLUMNS: readonly DataTableColumn<PipelineRun>[] = [
  {
    id: 'finished',
    header: 'Finished',
    cell: (r) => (r.finished_utc === null ? '—' : <time dateTime={r.finished_utc}>{r.finished_utc}</time>),
  },
  { id: 'source', header: 'Source', cell: (r) => r.source },
  {
    id: 'status',
    header: 'Status',
    cell: (r) => (
      <span data-tone={r.status === 'failed' ? 'bad' : r.status === 'cancelled' ? 'warn' : 'plain'} data-status={r.status}>
        {r.status}
      </span>
    ),
  },
  { id: 'requested_by', header: 'Requested by', cell: (r) => r.requested_by_upn },
  { id: 'approved_by', header: 'Approved by', cell: (r) => r.approved_by_upn ?? '—' },
  { id: 'filter', header: 'Filter', cell: (r) => summariseFilter(r.filter) },
  {
    id: 'counts',
    header: 'Seen / written / held / failed',
    cell: (r) => `${r.counts.seen} / ${r.counts.written} / ${r.counts.held} / ${r.counts.failed}`,
    cellClassName: 'num',
  },
  {
    id: 'error',
    header: 'Error',
    cell: (r) => (r.error_summary === null ? '—' : <span title={r.error_summary}>{truncate(r.error_summary)}</span>),
  },
];

export function RunHistory({ runs }: { readonly runs: readonly PipelineRun[] }) {
  return (
    <DataTable
      caption="Run history — the last twenty API-originated runs; break-glass CLI runs leave no row"
      columns={COLUMNS}
      rows={runs}
      rowKey={(r) => r.pipeline_run_id}
      empty={<p>No run has finished yet.</p>}
    />
  );
}
